import { Box, Typography, useTheme } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { DOWNY_MODEL, MELCAST_MODEL, POWDERY_MODEL, PROVISIONAL_POWDERY_THRESHOLD } from "../../config/diseaseRisk.config";
import { formatRiskDate } from "../../utils/diseaseRiskUtils";

const shortDate = (date) => formatRiskDate(date).slice(0, 5);
const axisProps = { tickLine: false, axisLine: false, tick: { fontSize: 12 }, minTickGap: 28 };

const RiskTooltip = ({ active, payload, label, model }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Box sx={{ p: 1.5, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="body2" fontWeight={600}>{formatRiskDate(label)}</Typography>
      {payload.map((item) => <Typography key={item.dataKey} variant="body2">{item.name}: {item.value ?? "—"}</Typography>)}
      {model === POWDERY_MODEL && point.action && <Typography variant="body2">{point.action}</Typography>}
      {model === DOWNY_MODEL && <>
        <Typography variant="body2">SIP: {point.sip_start ? "condizioni attivate" : "condizioni non attivate"}</Typography>
        <Typography variant="body2">Germinazione: {point.germination_start ? "condizioni attivate" : "condizioni non attivate"}</Typography>
      </>}
      {model === MELCAST_MODEL && <Typography variant="body2">Soglie raggiunte: {point.spray_recommendation ?? "—"}</Typography>}
    </Box>
  );
};

export const RiskHistoryChart = ({ data, model, selectedDate, onSelect, efiInterval }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.info.main;
  const isDowny = model === DOWNY_MODEL;
  const isMelcast = model === MELCAST_MODEL;
  const maxEfi = Math.max(0, ...data.map((point) => Number.isFinite(point.cumulative_efi) ? point.cumulative_efi : 0));
  const thresholdCount = efiInterval > 0 ? Math.max(1, Math.ceil(maxEfi / efiInterval)) : 0;
  // Keep long seasons legible; every crossing is still marked on the curve.
  const thresholdStride = Math.max(1, Math.ceil(thresholdCount / 6));
  const thresholds = [...new Set(Array.from({ length: Math.min(thresholdCount, 6) }, (_, i) =>
    Math.min((i + 1) * thresholdStride, thresholdCount) * efiInterval))];
  const handleClick = (event) => {
    const date = event?.activeLabel ?? data[Number(event?.activeTooltipIndex ?? event?.activeIndex)]?.date;
    if (data.some((point) => point.date === date && point.available)) onSelect(date);
  };
  const oidiumDot = ({ cx, cy, payload }) => {
    if (!Number.isFinite(payload.cumulative_severity)) return <g />;
    return <circle cx={cx} cy={cy} r={payload.cumulative_severity >= PROVISIONAL_POWDERY_THRESHOLD ? 5 : 3}
      fill={payload.cumulative_severity >= PROVISIONAL_POWDERY_THRESHOLD ? theme.palette.warning.main : primary} />;
  };
  return (
    <Box role="img" aria-label={isDowny ? "Ore favorevoli e condizioni attivate per data di valutazione" : isMelcast ? "EFI cumulato e superamenti delle soglie per giorno" : "Severità cumulata per data di valutazione, con soglia indicativa a 7"}>
      <ResponsiveContainer width="100%" height={320} minWidth={0}>
        {isDowny ? (
          <BarChart data={data} onClick={handleClick} margin={{ top: 16, right: 16, left: 24, bottom: 12 }} accessibilityLayer>
            <CartesianGrid vertical={false} stroke={theme.palette.divider} />
            <XAxis {...axisProps} dataKey="date" tickFormatter={shortDate} />
            <YAxis {...axisProps} domain={[0, 24]} allowDecimals={false} label={{ value: "Ore", angle: -90, position: "insideLeft" }} />
            <Tooltip content={<RiskTooltip model={model} />} cursor={{ fill: theme.palette.action.hover }} />
            <Legend />
            <Bar dataKey="n_sip_hours" name="Ore favorevoli SIP (max 8)" fill={primary} maxBarSize={20} isAnimationActive={false} />
            <Bar dataKey="n_germination_hours" name="Ore favorevoli germinazione (max 24)" fill={secondary} maxBarSize={20} isAnimationActive={false} />
            {data.filter((point) => point.sip_start).map((point) => <ReferenceDot key={`sip-${point.date}`} x={point.date} y={point.n_sip_hours} r={5} fill={primary} stroke={theme.palette.background.paper} />)}
            {data.filter((point) => point.germination_start).map((point) => <ReferenceDot key={`germ-${point.date}`} x={point.date} y={point.n_germination_hours}
              shape={({ cx, cy }) => <path d={`M${cx},${cy - 6}l6,6l-6,6l-6,-6Z`} fill={secondary} stroke={theme.palette.background.paper} />} />)}
            <ReferenceLine x={selectedDate} stroke={theme.palette.text.secondary} strokeDasharray="3 3" />
          </BarChart>
        ) : (
          <LineChart data={data} onClick={handleClick} margin={{ top: 24, right: 16, left: 24, bottom: 12 }} accessibilityLayer>
            <CartesianGrid vertical={false} stroke={theme.palette.divider} />
            <XAxis {...axisProps} dataKey="date" tickFormatter={shortDate} />
            <YAxis {...axisProps} allowDecimals={false} domain={isMelcast ? [0, (max) => Math.max(max, ...thresholds)] : [0, (max) => Math.max(12, max)]}
              label={{ value: isMelcast ? "EFI cumulato" : "Severità cumulata", angle: -90, position: "insideLeft" }} />
            <Tooltip content={<RiskTooltip model={model} />} />
            {isMelcast ? thresholds.map((value) => <ReferenceLine key={value} y={value} stroke={theme.palette.warning.main} strokeDasharray="5 5"
              label={{ value: `${value} EFI`, position: "insideTopLeft", fontSize: 12, fill: theme.palette.text.secondary }} />) : (
              <ReferenceLine y={PROVISIONAL_POWDERY_THRESHOLD} stroke={theme.palette.warning.main} strokeDasharray="5 5" />
            )}
            <Line type="linear" dataKey={isMelcast ? "cumulative_efi" : "cumulative_severity"} name={isMelcast ? "EFI cumulato" : "Severità cumulata (6 giorni)"}
              stroke={primary} strokeWidth={2} connectNulls={false} dot={isMelcast ? { r: 3 } : oidiumDot} activeDot={{ r: 6 }} isAnimationActive={false} />
            {isMelcast && data.filter((point) => point.thresholdReached && Number.isFinite(point.cumulative_efi)).map((point) => <ReferenceDot key={point.date}
              x={point.date} y={point.cumulative_efi} r={6} fill={theme.palette.warning.main} stroke={theme.palette.background.paper} />)}
            <ReferenceLine x={selectedDate} stroke={theme.palette.text.secondary} strokeDasharray="3 3" />
          </LineChart>
        )}
      </ResponsiveContainer>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
        {isMelcast ? "Data del record giornaliero" : "Data della valutazione"}
        {isDowny && " · ● SIP attivato · ◆ Germinazione attivata"}
        {isMelcast && efiInterval > 0 && ` · Soglie ogni ${efiInterval} EFI · Punti evidenziati: nuove soglie raggiunte`}
      </Typography>
    </Box>
  );
};

export const RiskDailyChart = ({ data, melcast = false }) => {
  const theme = useTheme();
  return <Box role="img" aria-label={melcast ? "Indice EFI giornaliero" : "Classi di severità giornaliera nella finestra selezionata"}>
    <ResponsiveContainer width="100%" height={210} minWidth={0}>
      <BarChart data={data} margin={{ top: 12, right: 16, left: 24, bottom: 0 }} accessibilityLayer>
        <CartesianGrid vertical={false} stroke={theme.palette.divider} />
        <XAxis {...axisProps} dataKey="date" tickFormatter={shortDate} />
        <YAxis {...axisProps} domain={melcast ? [0, "auto"] : [0, 2]} allowDecimals={false}
          label={{ value: melcast ? "EFI" : "Classe", angle: -90, position: "insideLeft" }} />
        <Tooltip labelFormatter={formatRiskDate} cursor={{ fill: theme.palette.action.hover }} />
        <Bar dataKey={melcast ? "efi" : "severity"} name={melcast ? "EFI giornaliero" : "Severità"} fill={theme.palette.primary.main} maxBarSize={36} isAnimationActive={false}>
          {!melcast && data.map((point) => <Cell key={point.date} fill={point.severity === 2 ? theme.palette.error.main : point.severity === 1 ? theme.palette.warning.main : theme.palette.primary.main} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
    {!melcast && <Typography variant="caption" color="text.secondary">Classi del modello: 0 = assente · 1 = moderata · 2 = severa</Typography>}
  </Box>;
};
